import {
  AbstractMesh,
  Color3,
  PBRMaterial,
  Quaternion,
  SceneLoader,
  StandardMaterial,
  Vector3,
  type AssetContainer,
  type Scene,
  type Skeleton,
  type TransformNode,
} from '@babylonjs/core';
import { createLogger, type BodyPartModifier, type EquipmentPiece, type ItemGrip, type SkeletonType, type VisualAttachmentSource } from '@sorrem/shared';
import { resolveAttachmentTransform } from './attachment-transform';
import type { CharacterLoader } from './character-loader';
import type { LoadedCharacterMesh } from './character-mesh';
import { arrayBufferToDataUri } from '../model-cache';
import { LoadOnceCache } from '../load-once-cache';
import { addHeldHalo, type HeldHalo } from '../vfx/model-halo';
import { attachItemEffect, type ItemLooks } from '../vfx/item-effect';
import { listMeshesUnder } from '../mesh-tree';

const logger = createLogger('engine:equipment-mesh-loader');

/** A single attached equipment item, returned so its slot can be individually replaced (live re-equip) or disposed. */
export interface AttachedEquipmentHandle {
  readonly slotId: string;
  readonly itemDefinitionId: string;
  readonly root: TransformNode;
  /** Steps its effects (an item effect, a held model's halo) by `deltaSeconds`; its character calls it every frame. */
  update(deltaSeconds: number): void;
  dispose(): void;
}

/** A handle with nothing that moves on its own. */
function updateNothing(): void {
  // A plain attached model has no effects to step.
}

/** What attachment resolution reads from an equipment catalog entry: where it attaches, and any outfit it stands for. */
export type EquipmentLookupEntry = VisualAttachmentSource & Pick<EquipmentPiece, 'outfitParts'>;

/** The body an equipped list is worn on, for outfit armor (see `expandOutfitItemIds`). */
export interface OutfitBody {
  readonly characterModelId: string;
  /** `CharacterModel.defaultOutfitItemId`: worn when nothing in the list is an outfit. */
  readonly defaultOutfitItemId?: string;
}

/**
 * Swaps each outfit armor in `itemIds` (an entry with `outfitParts`) for
 * its body parts on this body, in place. When none of them is an outfit
 * (an empty armor slot, or armor with no parts of its own), the body's
 * default outfit's parts are added at the end. Being last, they only take
 * slots nothing earlier in the list claimed (first-come, see
 * `resolveEquipmentAttachments`), so outfit pieces a character already
 * wears keep theirs.
 */
export function expandOutfitItemIds(
  itemIds: readonly string[],
  findEquipmentPiece: (itemDefinitionId: string) => EquipmentLookupEntry | undefined,
  body: OutfitBody,
): string[] {
  const expanded: string[] = [];
  let wearsOutfit = false;
  for (const itemId of itemIds) {
    const outfitParts = findEquipmentPiece(itemId)?.outfitParts;
    if (outfitParts) {
      wearsOutfit = true;
      expanded.push(...(outfitParts[body.characterModelId] ?? []));
    } else {
      expanded.push(itemId);
    }
  }
  if (!wearsOutfit && body.defaultOutfitItemId) {
    expanded.push(...(findEquipmentPiece(body.defaultOutfitItemId)?.outfitParts?.[body.characterModelId] ?? []));
  }
  return expanded;
}

/** A resolved (but not yet loaded) equipped item: which bone it attaches to and where to load its model from. */
export interface ResolvedEquipmentAttachment {
  readonly itemDefinitionId: string;
  readonly slotId: string;
  readonly boneName: string;
  readonly model: string;
  readonly positionOffset?: { x: number; y: number; z: number };
  readonly rotationOffset?: { x: number; y: number; z: number };
  readonly scale?: number;
  /** The item's grip (`VisualAttachmentSource.grip`), brought onto the bone before the offsets apply. */
  readonly grip?: ItemGrip;
  /** See `AttachmentSlot.attachmentType` - defaults to 'rigid' when the slot doesn't set one. */
  readonly attachmentType: 'rigid' | 'skinned';
  /** Whether this is gameplay equipment or a cosmetic body part. */
  readonly catalogType: 'equipment' | 'body-part';
}

/**
 * Pure resolution step of equipment attachment: given a character's
 * skeleton type and ways to look up equipment vs body parts,
 * determines which equipped items have a visible 3D model and which bone each
 * attaches to. No Babylon/Scene dependency - callers do the actual mesh
 * loading/parenting (see `attachEquipmentToCharacter`) - kept separate so
 * this resolution logic is unit-testable without a live Scene.
 *
 * Items with no catalog `.model` (icon-only, e.g. rings/amulets), no
 * catalog `.slot`, or whose slot has no bone mapping for this skeleton type
 * are silently skipped - equipping such an item is valid, it just has
 * nothing to render in 3D.
 *
 * Slots any listed item blocks (`VisualAttachmentSource.blocksSlots`, e.g. a
 * mohawk blocking a hood) are left empty: items meant for them are skipped,
 * whatever their order in the list.
 *
 * `equippedItemDefinitionIds` is a flat, unordered-by-slot list (it comes
 * from `resolveEquippedLoadoutSummary`/the Character Debugger's flattened
 * selection, neither of which tags each id with the specific slot it was
 * equipped into) - so when an item's `.slot` names more than one candidate
 * (e.g. a one-handed weapon usable in `weapon-main` or `weapon-off`),
 * there's no way to know here which one a given equip actually meant.
 * Resolved on a first-come, first-served basis in input order: each item
 * claims the first still-open candidate slot it lists. This is correct
 * whenever at most one equipped item is actually ambiguous (the common
 * case - e.g. previewing one new weapon in the off-hand slot while
 * everything else equipped has a single fixed slot), but if two *different*
 * multi-slot-capable items are equipped into specific, deliberately chosen
 * hands at once, whichever one appears first in
 * `equippedItemDefinitionIds` wins the slot both prefer, regardless of
 * which UI dropdown it was actually placed in. Fixing that would mean
 * threading explicit (itemDefinitionId, slotId) pairs through the whole
 * equip pipeline (network payloads included) instead of bare ids - out of
 * scope for the multi-slot feature itself.
 */
export function resolveEquipmentAttachments(
  skeletonType: SkeletonType,
  findEquipmentPiece: (itemDefinitionId: string) => EquipmentLookupEntry | undefined,
  findBodyPart: (itemDefinitionId: string) => VisualAttachmentSource | undefined,
  equippedItemDefinitionIds: readonly string[],
  body?: OutfitBody,
): ResolvedEquipmentAttachment[] {
  const resolved: ResolvedEquipmentAttachment[] = [];
  const claimedSlotIds = new Set<string>();
  const visualItemIds = body
    ? expandOutfitItemIds(equippedItemDefinitionIds, findEquipmentPiece, body)
    : equippedItemDefinitionIds;
  const blockedSlotIds = new Set(visualItemIds.flatMap((itemDefinitionId) =>
    (findEquipmentPiece(itemDefinitionId) ?? findBodyPart(itemDefinitionId))?.blocksSlots ?? []));

  for (const itemDefinitionId of visualItemIds) {
    let attachment: VisualAttachmentSource | undefined;
    let catalogType: 'equipment' | 'body-part' | undefined;

    const equipment = findEquipmentPiece(itemDefinitionId);
    if (equipment?.model && equipment.slot) {
      attachment = equipment;
      catalogType = 'equipment';
    } else {
      const bodyPart = findBodyPart(itemDefinitionId);
      if (bodyPart?.model && bodyPart.slot) {
        attachment = bodyPart;
        catalogType = 'body-part';
      }
    }

    if (!attachment || !catalogType) {
      continue;
    }

    const candidateSlotIds = Array.isArray(attachment.slot) ? attachment.slot : [attachment.slot];
    const attachmentSlot = skeletonType.attachmentSlots.find(
      (slot) => candidateSlotIds.includes(slot.id) && !claimedSlotIds.has(slot.id) && !blockedSlotIds.has(slot.id),
    );
    if (!attachmentSlot) {
      continue;
    }
    claimedSlotIds.add(attachmentSlot.id);

    resolved.push({
      itemDefinitionId,
      slotId: attachmentSlot.id,
      boneName: attachmentSlot.boneName,
      model: attachment.model!,
      // Item-level offset wins when present - see VisualAttachmentSource.positionOffset's
      // own comment for why not every item sharing a slot fits the same pivot.
      positionOffset: attachment.positionOffset ?? attachmentSlot.positionOffset,
      rotationOffset: attachment.rotationOffset ?? attachmentSlot.rotationOffset,
      scale: attachment.scale,
      grip: attachment.grip,
      attachmentType: attachmentSlot.attachmentType ?? 'rigid',
      catalogType,
    });
  }

  return resolved;
}

/**
 * Client-side asset cache for loaded visual attachments (equipment or body parts) 3D models.
 * Structurally mirrors `CharacterAssetCache` (value map + in-flight-promise
 * map, routed through `fetchModelWithCache` so a crucible-gathering-warmed model is
 * genuinely skipped rather than re-fetched - see `model-cache.ts`),
 * but keyed by the item's `model` path rather than a character id: multiple
 * item definitions can reference the same model file (reskins), and
 * multiple characters can wear the same item concurrently.
 *
 * Each instance is specialized to a single endpoint (equipment or body parts)
 * via the `fetchFn` parameter.
 */
export class AssetCache {
  private readonly containers = new LoadOnceCache<string, AssetContainer>((container) => container.dispose());

  constructor(
    private fetchFn: (itemDefinitionId: string) => Promise<ArrayBuffer>,
  ) {}

  private async loadContainer(
    scene: Scene,
    modelPath: string,
    itemDefinitionId: string,
  ): Promise<AssetContainer> {
    return this.containers.get(modelPath, () => this.fetchFn(itemDefinitionId)
      .then((modelData) => SceneLoader.LoadAssetContainerAsync('', arrayBufferToDataUri(modelData), scene)));
  }

  /** Loads an item model ahead of its first `instantiate`, so equipping it later loads nothing. */
  async preload(scene: Scene, modelPath: string, itemDefinitionId: string): Promise<void> {
    await this.loadContainer(scene, modelPath, itemDefinitionId);
  }

  /** Instantiate an independent copy of an item model, ready to be parented to a bone. */
  async instantiate(scene: Scene, modelPath: string, itemDefinitionId: string): Promise<TransformNode> {
    const container = await this.loadContainer(scene, modelPath, itemDefinitionId);
    const instantiated = container.instantiateModelsToScene((name) => name, false);
    const root = instantiated.rootNodes[0];
    if (!root) {
      instantiated.dispose();
      throw new Error(`Equipment model ${modelPath} imported zero root nodes.`);
    }
    // Only the root is handed out, so nothing can play the copy's own
    // animations: they go now. Its skeletons go with it (a skinned part has
    // already swapped its own for the character's and disposed it).
    instantiated.animationGroups.forEach((group) => group.dispose());
    root.onDisposeObservable.addOnce(() => {
      instantiated.skeletons.forEach((skeleton) => {
        if (scene.skeletons.includes(skeleton)) skeleton.dispose();
      });
    });

    // Babylon's glTF loader wraps every imported hierarchy in exactly this
    // node with a coordinate-system-flip transform (rotationQuaternion
    // [0,1,0,0] + scaling [1,1,-1]) whenever the scene is left-handed (the
    // default this project uses, confirmed never overridden) - that's how
    // it converts glTF's right-handed data into Babylon's left-handed
    // space. The character's own root carries the identical flip. Left in
    // place, parenting this root under the character's root (attachSkinnedPart)
    // or under one of its bones (the rigid path below) composes the SAME
    // flip twice, which cancels to identity - confirmed by direct matrix
    // inspection (fresh Node/Skeleton dump + world-matrix decompose) that
    // this leaves the equipped mesh's own transform back in its original
    // un-flipped orientation while its skin still deforms against the
    // character's correctly single-flipped skeleton, producing an X-axis
    // mirror between the two that's most visible on an asymmetric pose
    // (mid-stride) and nearly invisible on a symmetric one - exactly the
    // "outfit piece looks fine at rest, swings wrong mid-animation"
    // pattern reported. Clearing it here (once, centrally, before any
    // caller-applied offset) means every equipped item starts from a clean
    // identity local transform and inherits only the character's own
    // single flip, matching the coordinate frame its skin/bones actually
    // live in.
    const transformRoot = root as TransformNode;
    transformRoot.rotationQuaternion = Quaternion.Identity();
    transformRoot.scaling = Vector3.One();
    transformRoot.position = Vector3.Zero();

    return transformRoot;
  }

  dispose(): void {
    this.containers.clear();
  }
}

/**
 * Tints a loaded part's material(s) with a modifier's `primaryColor`
 * (e.g. a chosen hair color) by setting the material's base color - 
 * `albedoColor` for `PBRMaterial`, `diffuseColor` for `StandardMaterial`.
 * Both multiply against the material's existing texture rather than
 * replacing it, which is what lets a single grayscale/desaturated hair
 * texture be recolored convincingly instead of needing per-color texture
 * variants.
 *
 * Clones the material before tinting rather than mutating it in place:
 * `AssetCache.instantiate()` shares one material per model path across
 * every instance it produces (Babylon's `instantiateModelsToScene` is
 * called with `cloneMaterials: false`), so mutating it directly would
 * recolor every other character currently wearing the same hairstyle in
 * the same scene, not just this one.
 *
 * Returns the created clones so the caller can dispose them alongside the
 * mesh they're now assigned to - they're this instance's own, not shared
 * with `AssetCache`'s cached container, so nothing else will ever dispose
 * them otherwise.
 */
export function applyPrimaryColorTint(meshes: readonly AbstractMesh[], primaryColor: string): (PBRMaterial | StandardMaterial)[] {
  const color = Color3.FromHexString(primaryColor);
  const clonedByOriginal = new Map<PBRMaterial | StandardMaterial, PBRMaterial | StandardMaterial>();
  for (const mesh of meshes) {
    const material = mesh.material;
    if (material instanceof PBRMaterial || material instanceof StandardMaterial) {
      let clone = clonedByOriginal.get(material);
      if (!clone) {
        clone = material.clone(`${material.name}-tinted`) as PBRMaterial | StandardMaterial;
        if (clone instanceof PBRMaterial) {
          clone.albedoColor = color;
        } else {
          clone.diffuseColor = color;
        }
        clonedByOriginal.set(material, clone);
      }
      mesh.material = clone;
    }
  }
  return [...clonedByOriginal.values()];
}

/** Share of a held model's tint it also glows with, so a crystal reads as lit from within. */
const HELD_MODEL_GLOW = 0.55;

/**
 * Rigidly attaches a model held for a moment rather than equipped (e.g. a
 * crystal while it is being used) to `boneName`. With `tintColor` it is
 * tinted, glows in that color and sits in a faint pulsing halo of it, so a
 * palm-sized model still reads at game camera distance while the hand holding
 * it stays visible. The model is fitted to the socket the same way a weapon is
 * (see `fit-autosprite-weapon.mts`), so no offset is applied.
 *
 * Returns null when the character has no `boneName` node.
 */
export async function attachHeldModel(
  cache: AssetCache,
  scene: Scene,
  character: Pick<LoadedCharacterMesh, 'transformNodes'>,
  slotId: string,
  boneName: string,
  modelPath: string,
  tintColor?: string,
): Promise<AttachedEquipmentHandle | null> {
  const bone = character.transformNodes.find((node) => node.name === boneName);
  if (!bone) {
    return null;
  }

  const root = await cache.instantiate(scene, modelPath, modelPath);
  root.parent = bone;

  const meshes = listMeshesUnder(root);
  const tintedMaterials = tintColor ? applyPrimaryColorTint(meshes, tintColor) : [];
  let halo: HeldHalo | null = null;
  if (tintColor) {
    const color = Color3.FromHexString(tintColor);
    const glow = color.scale(HELD_MODEL_GLOW);
    tintedMaterials.forEach((material) => {
      material.emissiveColor = glow;
    });
    halo = addHeldHalo(scene, root, meshes, color);
  }

  return {
    slotId,
    itemDefinitionId: modelPath,
    root,
    update: (deltaSeconds: number): void => halo?.update(deltaSeconds),
    dispose: (): void => {
      // The tinted materials are this instance's own clones; see attachSkinnedPart.
      halo?.remove();
      tintedMaterials.forEach((material) => material.dispose());
      root.dispose();
    },
  };
}

/**
 * Attaches a 'skinned' equipment piece (a modular outfit part, hair, etc.):
 * rather than parenting the loaded model rigidly to one bone the way a
 * 'rigid' piece does, this rebinds the model's own skinned mesh(es) onto the
 * character's already-loaded skeleton, so the part deforms with body
 * animation exactly like the character's own body mesh does.
 *
 * Every part shares the same rig (confirmed by direct inspection: Quaternius'
 * base body, hair, and every modular outfit piece all carry the identical
 * 65-joint skeleton), but each is its own independent glTF import, so
 * Babylon gives it its own freshly-imported `Skeleton` object even though
 * the bone names/hierarchy match the character's. Reassigning
 * `mesh.skeleton` to the character's own skeleton (then disposing the
 * now-unused imported one) is the standard technique for multiple meshes
 * sharing one skeleton - confirmed to matter, not merely tidy: without it
 * the part would animate against its own frozen bind pose while the
 * character's skeleton moves under it.
 */
async function attachSkinnedPart(
  cache: AssetCache,
  scene: Scene,
  character: Pick<LoadedCharacterMesh, 'root' | 'skeletons'>,
  resolution: ResolvedEquipmentAttachment,
  assetBaseUrl: string,
  modifier?: BodyPartModifier,
): Promise<AttachedEquipmentHandle | null> {
  const sharedSkeleton = character.skeletons[0];
  if (!sharedSkeleton) {
    return null;
  }

  const root = await cache.instantiate(scene, resolution.model, resolution.itemDefinitionId);
  const partMeshes = listMeshesUnder(root);

  const skeletonsToDispose = new Set<Skeleton>();
  for (const mesh of partMeshes) {
    if (mesh.skeleton && mesh.skeleton !== sharedSkeleton) {
      skeletonsToDispose.add(mesh.skeleton);
      mesh.skeleton = sharedSkeleton;
    }
  }
  for (const skeleton of skeletonsToDispose) {
    skeleton.dispose();
  }

  const tintedMaterials = modifier?.primaryColor ? applyPrimaryColorTint(partMeshes, modifier.primaryColor) : [];

  // Kept in the character's own transform hierarchy for lifecycle/disposal
  // purposes only - a skinned mesh's world pose comes from its skin weights
  // against the shared skeleton, not from this node's own transform, so
  // (unlike the rigid path) positionOffset/scale on this node would have no
  // visible effect and are intentionally not applied here.
  root.parent = character.root;

  return {
    slotId: resolution.slotId,
    itemDefinitionId: resolution.itemDefinitionId,
    root,
    update: updateNothing,
    dispose: (): void => {
      // Tinted materials were cloned specifically for this instance (see
      // applyPrimaryColorTint) and aren't referenced by AssetCache's shared
      // container, so it's this handle's own responsibility to dispose
      // them - otherwise every hairstyle/color change during a session
      // (e.g. the character creation screen) leaks one material.
      tintedMaterials.forEach((material) => material.dispose());
      root.dispose();
    },
  };
}

/**
 * Resolves and attaches every equipped item that has a visible 3D model to
 * the matching bone (or, for a 'skinned' slot, the character's own skeleton)
 * on an already-loaded character (see `resolveEquipmentAttachments` for the
 * resolution rules).
 *
 * Takes a flat list of equipped item definition ids rather than either of
 * the two shapes callers actually hold (`CharacterEquipmentLoadout` for
 * players, `NpcModelState.equipment`'s slot-keyed map for NPCs) - the slot that
 * matters here is always the catalog's `EquipmentPiece.slot`, not either
 * input shape's own notion of slot, so callers flatten to ids first (e.g.
 * `resolveEquippedLoadoutSummary`, `@sorrem/shared`) rather than this
 * function sniffing which shape it was given.
 *
 * Every piece stays disabled until the whole batch has settled, then all are
 * enabled together, so a character never shows its outfit piece by piece
 * while larger models are still downloading.
 */
export async function attachEquipmentToCharacter(
  equipmentCache: AssetCache,
  bodyPartCache: AssetCache,
  characterLoader: CharacterLoader,
  scene: Scene,
  characterModelId: string,
  character: Pick<LoadedCharacterMesh, 'root' | 'skeletons' | 'transformNodes'>,
  equippedItemDefinitionIds: readonly string[],
  assetBaseUrl: string,
  outfitModifiers?: Partial<Record<string, BodyPartModifier>>,
  itemLooks?: ItemLooks,
): Promise<AttachedEquipmentHandle[]> {
  const skeletonType = characterLoader.findSkeletonType(characterModelId);
  if (!skeletonType) {
    return [];
  }

  const resolutions = resolveEquipmentAttachments(
    skeletonType,
    (itemDefinitionId) => characterLoader.findEquipmentPiece(itemDefinitionId),
    (itemDefinitionId) => characterLoader.findBodyPart(itemDefinitionId),
    equippedItemDefinitionIds,
    characterLoader.getOutfitBody(characterModelId),
  );

  // Settled, not `Promise.all` - these model files can be large (some
  // outfit pieces run into the tens of MB) and are fetched/parsed
  // concurrently, one per resolved attachment. `Promise.all` would let a
  // single slow/failed one (a timeout, a transient fetch error, a bad
  // catalog entry) reject the whole batch, silently discarding every other
  // attachment that already succeeded - visually indistinguishable from
  // "nothing is equipped" even when only one piece actually had a problem.
  const settled = await Promise.allSettled(
    resolutions.map(async (resolution): Promise<AttachedEquipmentHandle | null> => {
      const attached = await attachResolvedPart(equipmentCache, bodyPartCache, scene, character, resolution, assetBaseUrl, outfitModifiers);
      const look = itemLooks?.[resolution.itemDefinitionId];
      const handle = attached && look ? withItemEffect(scene, attached, look) : attached;
      // Hidden until the rest of the batch is in (see the doc comment above).
      handle?.root.setEnabled(false);
      return handle;
    }),
  );

  const handles: AttachedEquipmentHandle[] = [];
  settled.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      if (result.value) {
        handles.push(result.value);
      }
      return;
    }
    logger.error(`Failed to attach ${resolutions[index]!.itemDefinitionId}:`, result.reason);
  });

  handles.forEach((handle) => handle.root.setEnabled(true));
  return handles;
}

/** An attached item drawn with its rarity and upgrade effect (vfx/item-effect.ts), which goes when the item does. */
function withItemEffect(scene: Scene, handle: AttachedEquipmentHandle, look: ItemLooks[string]): AttachedEquipmentHandle {
  const meshes = listMeshesUnder(handle.root);
  const effect = attachItemEffect(scene, handle.root, meshes, look);
  return {
    ...handle,
    update: (deltaSeconds: number): void => {
      handle.update(deltaSeconds);
      effect.update(deltaSeconds);
    },
    dispose: (): void => {
      effect.remove();
      handle.dispose();
    },
  };
}

/** Instantiates one resolved attachment and parents it onto the character: skinned onto its skeleton, rigid onto its bone. */
async function attachResolvedPart(
  equipmentCache: AssetCache,
  bodyPartCache: AssetCache,
  scene: Scene,
  character: Pick<LoadedCharacterMesh, 'root' | 'skeletons' | 'transformNodes'>,
  resolution: ResolvedEquipmentAttachment,
  assetBaseUrl: string,
  outfitModifiers?: Partial<Record<string, BodyPartModifier>>,
): Promise<AttachedEquipmentHandle | null> {
  const cache = resolution.catalogType === 'body-part' ? bodyPartCache : equipmentCache;

  if (resolution.attachmentType === 'skinned') {
    return attachSkinnedPart(cache, scene, character, resolution, assetBaseUrl, outfitModifiers?.[resolution.slotId]);
  }

  const bone = character.transformNodes.find((node) => node.name === resolution.boneName);
  if (!bone) {
    return null;
  }

  const root = await cache.instantiate(scene, resolution.model, resolution.itemDefinitionId);
  root.parent = bone;
  // The grip first, then the offsets, as one transform (`instantiate` left the root at identity).
  const transform = resolveAttachmentTransform(resolution);
  root.position.copyFrom(transform.position);
  root.rotationQuaternion = transform.rotation;
  root.scaling.setAll(transform.scaling);

  return {
    slotId: resolution.slotId,
    itemDefinitionId: resolution.itemDefinitionId,
    root,
    update: updateNothing,
    dispose: (): void => root.dispose(),
  };
}
